import React, { useMemo } from 'react';

import { expValEquals, expVal } from '@confluence/feature-experiments';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { HydrationSafeComponent } from '@confluence/hydration-utils/entry-points/HydrationSafeComponent.tsx';

import { getSSRSmartLinksStore, getSSRSmartLinksBoundaryMap } from './ssrSmartLinksStore.ts';

// Suspense wrapper to be passed to ADF Renderer
function SuspenseWrapperForUrl({ url, children }) {
	if (!url || !expVal('confluence_smartlinks_suspense', 'isEnabled', false)) {
		return children;
	}
	// uses live store for lookup
	const boundaryMap = getSSRSmartLinksBoundaryMap();
	const fetcherName = boundaryMap[url];

	// if no fetcherName, assume no fetcher was created and don't wrap children
	if (!fetcherName) {
		return children;
	}

	return (
		<SuspenseWithDataFetcher dataFetcherName={fetcherName}>
			<HydrationSafeComponent isEnabled>{children}</HydrationSafeComponent>
		</SuspenseWithDataFetcher>
	);
}

// Put initialState into a getter
export const useStoreOptions = (
	initialState: any,
	isPageSSRd: boolean,
	options?: { useSSRStore?: boolean },
): any => {
	return useMemo(() => {
		const streamingStoreState = options?.useSSRStore ? getSSRSmartLinksStore() : initialState;

		const result: any = {};

		// Only include SuspenseWrapperForUrl if page was SSR'd
		if (isPageSSRd) {
			result.SuspenseWrapperForUrl = SuspenseWrapperForUrl;
		}

		// Include storeOptions if streamingStoreState exists and either:
		// - page was SSR'd, OR
		// - local cache is enabled
		if (
			streamingStoreState &&
			(isPageSSRd || expValEquals('platform_editor_smartlink_local_cache', 'isEnabled', true))
		) {
			result.storeOptions = {
				initialState: streamingStoreState,
			};
		}

		return result;
	}, [initialState, isPageSSRd, options?.useSSRStore]);
};
